import { Relation, TC, Ty } from '@cotera/era';
import { DataGridColumnConfig, Filter } from './types';
import { Column } from '../components/data-grid';
import { Assert } from '@cotera/utilities';
import { IconName } from '@cotera/client/app/components';
import { DG } from '@cotera/sdk/core';

/**
 * This list may contain more options than the standard system columns.
 *
 * For example, __row_number is not a system column but is one that is automatically added
 * for csv uploads (in order to create a unique primary key)
 */
const HIDDEN_COLUMNS = ['__cotera_stable_id', '__pk', '__row_number'];

export const displayTyWrapper = (
  config: 'llm' | 'eraql' | 'workflow' | 'assumption',
  subTy: Ty.ExtendedAttributeType
) => {
  if (config === 'llm') {
    return `llm<${Ty.displayTy(subTy)}>`;
  }
  if (config === 'eraql') {
    return `expr<${Ty.displayTy(subTy)}>`;
  }
  return Ty.displayTy(subTy);
};

export const columnsFromRel = (
  rel: Relation,
  getColumnConfigType?: (key: string) => DataGridColumnConfig['t'],
  order?: string[]
): Column[] => {
  const columns = Object.entries(rel.attributes).map(([key, _]) => {
    const config = getColumnConfigType?.(key) ?? 'assumption';

    const ty = rel.attributes[key]!;

    return {
      key: key as keyof typeof rel.attributes,
      title: key,
      accessorKey: key,
      type: displayTyWrapper(config, ty),
      id: key,
      status: HIDDEN_COLUMNS.includes(key) ? 'hidden' : 'visible',
      icon: iconForColumn(config, ty),
      t: config,
      pinned: null,
      width: undefined,
      inView: false,
    };
  });

  // Sort with priority: id columns first (shortest id first), then alphabetically
  return (
    columns.sort((a, b) => {
      const aKey = String(a.key);
      const bKey = String(b.key);
      const aHasId = aKey.includes('id');
      const bHasId = bKey.includes('id');

      // If both have 'id' or neither has 'id', sort by length then alphabetically
      if (aHasId === bHasId) {
        // If both are id columns, sort by length first (shortest first)
        if (aHasId) {
          const lengthDiff = aKey.length - bKey.length;
          if (lengthDiff !== 0) {
            return lengthDiff;
          }
        }
        // Then sort alphabetically
        return aKey.localeCompare(bKey);
      }

      // If only one has 'id', prioritize the one with 'id'
      return aHasId ? -1 : 1;
    }) as Column[]
  ).sort((a, b) => {
    const aIndex = order?.indexOf(a.key) ?? -1;
    const bIndex = order?.indexOf(b.key) ?? -1;

    // If both columns are in the order array, sort by their position
    if (aIndex >= 0 && bIndex >= 0) {
      return aIndex - bIndex;
    }

    // If only one column is in the order array, prioritize the ordered one
    if (aIndex >= 0 && bIndex < 0) {
      return -1;
    }
    if (aIndex < 0 && bIndex >= 0) {
      return 1;
    }

    // If neither column is in the order array, maintain their existing order
    return 0;
  });
};

/**
 * Toggles a category filter for a specific column
 * @param currentFilters - Current array of filters
 * @param columnName - Name of the column to filter
 * @param category - Category value to toggle
 * @returns New array of filters with the category toggled
 */
export const toggleCategoryFilter = (
  currentFilters: Filter[],
  columnName: string,
  category: string | null
): Filter[] => {
  const filterForColumn = currentFilters.find(
    (f) => f.column === columnName && f.filter.t === 'category'
  ) ?? {
    column: columnName,
    filter: {
      t: 'category',
      value: [],
    },
  };

  Assert.assert(filterForColumn.filter.t === 'category');
  const isExistingFilter = filterForColumn.filter.value.includes(category);

  const nextFilterValue = isExistingFilter
    ? filterForColumn.filter.value.filter((c) => c !== category)
    : [...(filterForColumn.filter.value ?? []), category];

  if (nextFilterValue.length === 0) {
    return currentFilters.filter((f) => f.column !== columnName);
  }

  return [
    ...currentFilters.filter((f) => f.column !== columnName),
    {
      column: columnName,
      filter: { t: 'category', value: nextFilterValue },
    },
  ];
};

export const toggleContinuousFilter = (
  currentFilters: Filter[],
  columnName: string,
  value: { min: number; max: number }
): Filter[] => {
  const filterForColumn = currentFilters.find(
    (f) => f.column === columnName && f.filter.t === 'continuous'
  ) ?? {
    column: columnName,
    filter: {
      t: 'continuous',
      value: [],
    },
  };

  Assert.assert(filterForColumn.filter.t === 'continuous');
  const isExistingFilter = filterForColumn.filter.value.some(
    (f) => f.min === value.min && f.max === value.max
  );

  const nextFilterValue = isExistingFilter
    ? filterForColumn.filter.value.filter(
        (c) => c.min !== value.min && c.max !== value.max
      )
    : [...(filterForColumn.filter.value ?? []), value];

  if (nextFilterValue.length === 0) {
    return currentFilters.filter((f) => f.column !== columnName);
  }

  return [
    ...currentFilters.filter((f) => f.column !== columnName),
    {
      column: columnName,
      filter: { t: 'continuous', value: nextFilterValue },
    },
  ];
};

export const iconForGraphColumn = (
  graph: DG.BuildExplorer,
  column: {
    rel: string;
    name: string;
  }
) => {
  const graphCol = graph.getColumn({
    rel: column.rel,
    name: column.name,
  });
  const columnType = graphCol?.config?.t ?? 'assumption';

  const columnDataType = graphCol?.ty ?? null;

  // Use a default type if we can't get the actual type
  const fallbackType = Ty.ty('string');

  return iconForColumn(columnType, columnDataType ?? fallbackType);
};

export const iconForColumn = (
  colType: 'llm' | 'eraql' | 'assumption' | 'workflow',
  rawTy: Ty.ExtendedAttributeType
): IconName => {
  if (colType === 'llm') {
    return 'sparkles';
  }
  if (colType === 'eraql') {
    return 'variable';
  }

  if (colType === 'workflow') {
    return 'workflow';
  }

  Assert.assert(colType === 'assumption');

  if (TC.isStruct(rawTy)) {
    return 'braces';
  }
  if (TC.isNumeric(rawTy)) {
    return 'hash';
  }
  if (TC.isBoolean(rawTy)) {
    return 'check';
  }
  if (TC.isTimestamp(rawTy)) {
    return 'calendar';
  }
  if (TC.isStringLike(rawTy)) {
    return 'text';
  }

  return 'text';
};
